import '../diseñocss/SectoresDark.css';
import axios from "axios";
import { useState, useEffect, useCallback } from "react";
import * as XLSX from 'xlsx';
import ConfiguradorEtiquetas from '../Modal/ConfiguradorEtiquetas';
import jsPDF from 'jspdf';
import { Tooltip } from "@mui/material";
import {
    Ticket,
    Settings2,
    Printer,
    Download,
    Calendar,
    MapPin,
    Building2,
    FileText
} from 'lucide-react';
import { useTheme } from "../guardarToken/themeContext";

const apiUrlPedidos = process.env.REACT_APP_API_PEDIDOS;
const apiUrlSedes = process.env.REACT_APP_API_SEDES || '/api/sedes/';
const apiUrlTiposMenu = process.env.REACT_APP_API_TIPOS_MENU || '/api/menus/tipos-menu';
const apiUrlSectores = process.env.REACT_APP_API_SECTORES || '/api/sectores/section/';

const normalizarTextoEtiqueta = (valor) => String(valor || '').trim();
const formatFechaISO = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
const getProximaSemanaRange = () => {
    const hoy = new Date();
    const day = (hoy.getDay() + 6) % 7;
    const lunesActual = new Date(hoy);
    lunesActual.setHours(0, 0, 0, 0);
    lunesActual.setDate(hoy.getDate() - day);

    const lunesProximo = new Date(lunesActual);
    lunesProximo.setDate(lunesActual.getDate() + 7);

    const domingoProximo = new Date(lunesProximo);
    domingoProximo.setDate(lunesProximo.getDate() + 6);

    return {
        desde: formatFechaISO(lunesProximo),
        hasta: formatFechaISO(domingoProximo),
    };
};
const isFechaDentroDeRango = (fecha, desde, hasta) => {
    const fechaNormalizada = String(fecha || '').slice(0, 10);
    return fechaNormalizada >= desde && fechaNormalizada <= hasta;
};
const getPedidoUniqueKey = (pedido) => [
    pedido?.fecha,
    pedido?.usuario,
    pedido?.id_plato,
    pedido?.nombre,
    pedido?.tipo,
    pedido?.sede_id,
    pedido?.sector,
    pedido?.ubicacion,
    pedido?.observacion,
].map((value) => String(value ?? '')).join('||');
const dedupeNonInvitadoPedidos = (pedidos) => {
    const seen = new Set();
    return pedidos.filter((pedido) => {
        if (String(pedido?.tipo_usuario || '').toLowerCase() === 'invitado') {
            return true;
        }
        const key = getPedidoUniqueKey(pedido);
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
};

const limpiarUbicacionDuplicada = (ubicacion, sector) => {
    const ubicacionNormalizada = normalizarTextoEtiqueta(ubicacion);
    const sectorNormalizado = normalizarTextoEtiqueta(sector);

    if (!ubicacionNormalizada || !sectorNormalizado) {
        return ubicacionNormalizada;
    }

    const ubicacionUpper = ubicacionNormalizada.toUpperCase();
    const sectorUpper = sectorNormalizado.toUpperCase();
    const sufijosDuplicados = [
        ` - ${sectorUpper}`,
        `, ${sectorUpper}`,
        ` ${sectorUpper}`,
    ];

    const sufijo = sufijosDuplicados.find((item) => ubicacionUpper.endsWith(item));
    if (!sufijo) {
        return ubicacionNormalizada;
    }

    return ubicacionNormalizada.slice(0, -sufijo.length).trim().replace(/[-,]$/, '').trim();
};

const getSectorEtiqueta = (pedido) => normalizarTextoEtiqueta(pedido?.sector);
const getUbicacionEtiqueta = (pedido) => limpiarUbicacionDuplicada(pedido?.ubicacion, pedido?.sector);
const getDireccionEtiqueta = (pedido) => getUbicacionEtiqueta(pedido) || normalizarTextoEtiqueta(pedido?.sede_direccion);
const CAMPOS_FECHA_REQUERIDOS = [
    { id: 'fechaElaboracion', label: 'Elab.', x: 55, y: 28, fontSize: 9, visible: true, prefijo: 'Elaborado: ', rotation: 0 },
    { id: 'fechaVencimiento', label: 'Vto.', x: 55, y: 35, fontSize: 9, visible: true, prefijo: 'Consumir hasta: ', rotation: 0 },
];
const asegurarCamposFecha = (campos = []) => {
    const lista = Array.isArray(campos) ? [...campos] : [];
    CAMPOS_FECHA_REQUERIDOS.forEach((campoRequerido) => {
        if (!lista.some((campo) => campo.id === campoRequerido.id)) {
            lista.push({ ...campoRequerido });
        }
    });
    return lista.map((campo) => {
        if (campo.id === 'fechaElaboracion') {
            return { ...campo, prefijo: campo.prefijo || 'Elaborado: ' };
        }
        if (campo.id === 'fechaVencimiento') {
            return { ...campo, prefijo: campo.prefijo || 'Consumir hasta: ' };
        }
        return campo;
    });
};
const matchesSedeFilter = (pedido, sedeFilter) => sedeFilter === 'todas' || String(pedido?.sede_id) === String(sedeFilter);
const matchesTipoFilter = (pedido, tipoFilter) => tipoFilter === 'todos' || String(pedido?.tipo || '') === tipoFilter;
const matchesSectorFilter = (pedido, sectorFilter) => sectorFilter === 'todos' || String(pedido?.sector || '') === sectorFilter;

function AbmPedidos() {
    const { isDarkMode } = useTheme();
    const rangoInicial = getProximaSemanaRange();
    const [pedidos, setPedidos] = useState([]);
    const [selectedPedidos, setSelectedPedidos] = useState([]);
    const [showConfigurador, setShowConfigurador] = useState(false);
    const [etiquetaConfig, setEtiquetaConfig] = useState(null);
    const [sedes, setSedes] = useState([]);
    const [tiposMenu, setTiposMenu] = useState([]);
    const [sectoresCatalogo, setSectoresCatalogo] = useState([]);
    const [sedeFilter, setSedeFilter] = useState('todas');
    const [tipoFilter, setTipoFilter] = useState('todos');
    const [sectorFilter, setSectorFilter] = useState('todos');
    const [fechaDesde, setFechaDesde] = useState(rangoInicial.desde);
    const [fechaHasta, setFechaHasta] = useState(rangoInicial.hasta);
    const tiposDesdePedidos = [...new Set(
        pedidos
            .map((pedido) => String(pedido?.tipo || '').trim())
            .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    const tiposDisponibles = (tiposMenu.length > 0 ? tiposMenu : tiposDesdePedidos);
    const sectoresDesdePedidos = [...new Set(
        pedidos
            .map((pedido) => String(pedido?.sector || '').trim())
            .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    const sectoresDisponibles = (sectoresCatalogo.length > 0 ? sectoresCatalogo : sectoresDesdePedidos);
    const pedidosVisibles = dedupeNonInvitadoPedidos(
        pedidos.filter((pedido) =>
            matchesSedeFilter(pedido, sedeFilter) &&
            matchesTipoFilter(pedido, tipoFilter) &&
            matchesSectorFilter(pedido, sectorFilter)
        )
    ).sort((a, b) => {
        const dirA = (a.ubicacion || a.sede_direccion || '').toLowerCase();
        const dirB = (b.ubicacion || b.sede_direccion || '').toLowerCase();
        if (dirA !== dirB) return dirA.localeCompare(dirB, 'es');
        const tipoA = (a.tipo || '').toLowerCase();
        const tipoB = (b.tipo || '').toLowerCase();
        return tipoA.localeCompare(tipoB, 'es');
    });

    const getPedidos = useCallback(async () => {
        try {
            const params = {};
            if (fechaDesde) params.desde = fechaDesde;
            if (fechaHasta) params.hasta = fechaHasta;

            const res = await axios.get(apiUrlPedidos, { params });
            const pedidosFiltrados = Array.isArray(res.data)
                ? res.data.filter((pedido) => {
                    if (fechaDesde && !fechaHasta) return String(pedido?.fecha || '').slice(0, 10) >= fechaDesde;
                    if (!fechaDesde && fechaHasta) return String(pedido?.fecha || '').slice(0, 10) <= fechaHasta;
                    if (fechaDesde && fechaHasta) return isFechaDentroDeRango(pedido?.fecha, fechaDesde, fechaHasta);
                    return true;
                })
                : [];
            setPedidos(pedidosFiltrados);
        } catch (error) {
            console.error('Error al obtener los pedidos:', error);
        }
    }, [fechaDesde, fechaHasta]);

    const getSedes = useCallback(async () => {
        try {
            const res = await axios.get(apiUrlSedes);
            setSedes(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error('Error al obtener las sedes:', error);
            setSedes([]);
        }
    }, []);

    const getTiposMenu = useCallback(async () => {
        try {
            const res = await axios.get(apiUrlTiposMenu);
            const tipos = Array.isArray(res.data)
                ? res.data
                    .map((tipo) => String(tipo?.nombre || '').trim())
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
                : [];
            setTiposMenu(tipos);
        } catch (error) {
            console.error('Error al obtener los tipos de menú:', error);
            setTiposMenu([]);
        }
    }, []);

    const getSectoresCatalogo = useCallback(async () => {
        try {
            const res = await axios.get(apiUrlSectores);
            const sectores = Array.isArray(res.data)
                ? res.data
                    .map((sector) => String(sector?.sector || '').trim())
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
                : [];
            setSectoresCatalogo([...new Set(sectores)]);
        } catch (error) {
            console.error('Error al obtener los sectores:', error);
            setSectoresCatalogo([]);
        }
    }, []);

    useEffect(() => {
        getPedidos();
        getSedes();
        getTiposMenu();
        getSectoresCatalogo();
    }, [getPedidos, getSedes, getTiposMenu, getSectoresCatalogo]);

    useEffect(() => {
        if (tipoFilter !== 'todos' && !tiposDisponibles.includes(tipoFilter)) {
            setTipoFilter('todos');
        }
    }, [tipoFilter, tiposDisponibles]);

    useEffect(() => {
        if (sectorFilter !== 'todos' && !sectoresDisponibles.includes(sectorFilter)) {
            setSectorFilter('todos');
        }
    }, [sectorFilter, sectoresDisponibles]);

    useEffect(() => {
        const pedidosVisiblesIds = new Set(pedidosVisibles.map((pedido) => pedido.id_pedido));
        setSelectedPedidos((prev) => {
            const filtered = prev.filter((id) => pedidosVisiblesIds.has(id));
            return filtered.length === prev.length ? prev : filtered;
        });
    }, [pedidosVisibles]);

    // Cargar configuración de etiqueta guardada desde el servidor
    useEffect(() => {
        const loadConfig = async () => {
            try {
                const apiUrlEtiquetas = process.env.REACT_APP_API_ETIQUETAS || '/api/menus/etiquetas/';
                const res = await axios.get(`${apiUrlEtiquetas}plantillas/activa`);

                if (res.data) {
                    const parsedCampos = typeof res.data.campos === 'string'
                        ? JSON.parse(res.data.campos)
                        : res.data.campos;
                    const config = {
                        campos: asegurarCamposFecha(parsedCampos.campos || parsedCampos),
                        tamanoEtiqueta: parsedCampos.tamanoEtiqueta || '100x50'
                    };
                    console.log('✅ Configuración de etiqueta activa cargada desde DB:', config);
                    setEtiquetaConfig(config);
                } else {
                    // Fallback a localStorage
                    const savedConfig = localStorage.getItem('etiquetaConfig');
                    if (savedConfig) {
                        const parsed = JSON.parse(savedConfig);
                        setEtiquetaConfig({
                            campos: asegurarCamposFecha(parsed.campos),
                            tamanoEtiqueta: parsed.tamanoEtiqueta || '100x50'
                        });
                    }
                }
            } catch (error) {
                console.error('Error al cargar configuración de etiquetas desde DB:', error);
                const savedConfig = localStorage.getItem('etiquetaConfig');
                if (savedConfig) {
                    const parsed = JSON.parse(savedConfig);
                    setEtiquetaConfig({
                        campos: asegurarCamposFecha(parsed.campos),
                        tamanoEtiqueta: parsed.tamanoEtiqueta || '100x50'
                    });
                }
            }
        };

        loadConfig();
    }, []);

    // Recargar config cuando se guarda desde el modal (escuchar cambios en localStorage)
    useEffect(() => {
        const handleStorageChange = () => {
            const savedConfig = localStorage.getItem('etiquetaConfig');
            if (savedConfig) {
                try {
                    const parsed = JSON.parse(savedConfig);
                    setEtiquetaConfig({
                        campos: asegurarCamposFecha(parsed.campos),
                        tamanoEtiqueta: parsed.tamanoEtiqueta || '100x50'
                    });
                } catch (error) {
                    console.error('Error al actualizar configuración:', error);
                }
            }
        };

        // Escuchar cambios en localStorage (funciona en pestañas distintas)
        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    // Función para generar ZPL usando configuración personalizada o predeterminada
    const generateZPLFromConfig = (pedido, config) => {
        const TAMANOS_ETIQUETA = {
            '100x50': { width: 100, height: 50 },
            '63x31': { width: 63, height: 31 },
        };
        const tamano = TAMANOS_ETIQUETA[config?.tamanoEtiqueta] || TAMANOS_ETIQUETA['100x50'];
        const LABEL_WIDTH = tamano.width;
        const LABEL_HEIGHT = tamano.height;
        const DPI = 203;
        const MM_TO_DOTS = DPI / 25.4;

        const dotsWidth = Math.round(LABEL_WIDTH * MM_TO_DOTS);
        const dotsHeight = Math.round(LABEL_HEIGHT * MM_TO_DOTS);

        let zpl = `^XA\n^PW${dotsWidth}\n^LL${dotsHeight}\n`;

        // Parsear fecha del pedido correctamente (evitar desfase UTC)
        const [anio, mes, dia] = (pedido.fecha || '').split('-').map(Number);
        const fechaPedido = new Date(anio, mes - 1, dia);
        const fechaVencimiento = new Date(anio, mes - 1, dia + 1);
        const sectorEtiqueta = getSectorEtiqueta(pedido);
        const ubicacionEtiqueta = getUbicacionEtiqueta(pedido);
        const direccionEtiqueta = getDireccionEtiqueta(pedido);

        if (config && config.campos) {
            const camposConfigurados = asegurarCamposFecha(config.campos);
            // Usar configuración personalizada
            camposConfigurados.filter(c => c.visible).forEach(campo => {
                const xDots = Math.round(campo.x * MM_TO_DOTS);
                const yDots = Math.round(campo.y * MM_TO_DOTS);
                const fontHeight = Math.round((campo.fontSize || 16) * 1.5);
                const rot = (campo.rotation || 0);
                const rotMap = { 0: 'N', 90: 'R', 180: 'I', 270: 'B' };
                const orient = rotMap[rot] || 'N';

                // Generar contenido según el tipo de campo
                if (campo.id === 'separador') {
                    // Línea horizontal de 80mm por defecto
                    zpl += `^FO${xDots},${yDots}^GB${Math.round(80 * MM_TO_DOTS)},3,3^FS\n`;
                    return;
                }

                if (campo.id === 'qrCode') {
                    // Código QR: usar ^BQN,2,<mag>
                    const qrSizeMm = campo.qrSize || 30; // tamaño aproximado en mm
                    const mag = Math.max(2, Math.min(10, Math.round(qrSizeMm / 4))); // 1..10
                    const qrData = `${pedido.id_pedido || pedido.id || ''}|${pedido.nombreuser || ''}|${(pedido.nombre || '').slice(0, 60)}`;
                    zpl += `^FO${xDots},${yDots}^BQN,2,${mag}^FDLA,${qrData}^FS\n`;
                    return;
                }

                if (campo.id === 'barcode') {
                    // Código de barras Code128: ^BCN,<altura>,Y,N,N
                    const barHeightDots = Math.max(20, Math.round((campo.barcodeHeight || 50) * MM_TO_DOTS));
                    const code = `${pedido.id_pedido || pedido.id || ''}`;
                    zpl += `^FO${xDots},${yDots}^BC${orient},${barHeightDots},Y,N,N^FD${code}^FS\n`;
                    return;
                }

                // Caso general: texto
                let texto = '';
                switch (campo.id) {
                    case 'header':
                        texto = 'Def-Software';
                        break;
                    case 'nombre':
                        texto = `${campo.prefijo || ''}${pedido.nombreuser || ''}`;
                        break;
                    case 'apellido':
                        texto = `${campo.prefijo || ''}${pedido.apellido || ''}`;
                        break;
                    case 'nombreCompleto':
                        texto = `${campo.prefijo || ''}${pedido.nombreuser || ''} ${pedido.apellido || ''}`;
                        break;
                    case 'plato':

                        texto = `${campo.prefijo || ''}${pedido.nombre || ''}`;
                        break;
                    case 'tipoPlato':
                        texto = `${campo.prefijo || ''}${pedido.tipo || ''}`;
                        break;
                    case 'sector':
                        texto = `${campo.prefijo || ''}${sectorEtiqueta}`;
                        break;
                    case 'ubicacion':
                        texto = `${campo.prefijo || ''}${ubicacionEtiqueta}`;
                        break;
                    case 'sede':
                        texto = `${campo.prefijo || ''}${pedido.sede_nombre || ''}`;
                        break;
                    case 'sede_direccion':
                        texto = `${campo.prefijo || ''}${direccionEtiqueta}`;
                        break;
                    case 'fechaElaboracion':
                        texto = `${campo.prefijo || 'Elaborado: '}${fechaPedido.toLocaleDateString('es-AR')}`;
                        break;
                    case 'fechaVencimiento':
                        texto = `${campo.prefijo || 'Consumir hasta: '}${fechaVencimiento.toLocaleDateString('es-AR')}`;
                        break;
                    default:
                        texto = campo.label || '';
                }

                zpl += `^FO${xDots},${yDots}^A0${orient},${fontHeight},${fontHeight}^FD${texto}^FS\n`;
            });
        } else {
            // Configuración predeterminada
            const fechaPedidoStr = fechaPedido.toLocaleDateString('es-AR');
            const fechaVtoStr = fechaVencimiento.toLocaleDateString('es-AR');
            zpl += `^FO50,30^ADN,20,10^FDDef-Software^FS
^FO50,60^ADN,18,9^FD${(pedido.nombreuser || '').trim()} ${(pedido.apellido || '').trim()}^FS
^FO50,85^ADN,14,7^FD${sectorEtiqueta}^FS
^FO50,105^ADN,14,7^FD${direccionEtiqueta}^FS
^FO50,140^ADN,16,8^FD${pedido.nombre || ''}^FS
^FO280,60^ADN,12,6^FDElaboracion:^FS
^FO280,85^ADN,16,8^FD${fechaPedidoStr}^FS
^FO280,110^ADN,12,6^FDConsumir hasta:^FS
^FO280,135^ADN,16,8^FD${fechaVtoStr}^FS
^FO270,30^GB1,140,2^FS\n`;
        }

        zpl += `^XZ\n`;
        return zpl;
    };

    const exportToExcel = (pedidosParaExportar = null) => {
        const pedidosAExportar = pedidosParaExportar || pedidosVisibles;

        if (pedidosAExportar.length === 0) {
            alert('No hay pedidos para exportar');
            return;
        }

        // Hoja 1: Detalle de pedidos
        const dataToExport = pedidosAExportar.map(order => ({
            Plato: order.nombre,
            Tipo: order.tipo,
            Fecha: order.fecha,
            Nombre: order.nombreuser,
            Apellido: order.apellido,
            Sector: order.sector,
            Sede: order.sede_nombre,
            Dirección: getDireccionEtiqueta(order),
            Observación: order.observacion
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Pedidos");

        // Hoja 2: Resumen por fecha y tipo de menú
        const resumenPorFechaTipo = {};
        pedidosAExportar.forEach(pedido => {
            const fecha = pedido.fecha;
            const tipo = pedido.tipo || 'Sin tipo';
            const sede = pedido.sede_nombre || 'Sin sede';

            if (!resumenPorFechaTipo[fecha]) {
                resumenPorFechaTipo[fecha] = {};
            }
            if (!resumenPorFechaTipo[fecha][sede]) {
                resumenPorFechaTipo[fecha][sede] = {};
            }
            if (!resumenPorFechaTipo[fecha][sede][tipo]) {
                resumenPorFechaTipo[fecha][sede][tipo] = 0;
            }
            resumenPorFechaTipo[fecha][sede][tipo]++;
        });

        // Convertir a array para la segunda hoja
        const resumenArray = [];
        Object.keys(resumenPorFechaTipo).sort().forEach(fecha => {
            Object.keys(resumenPorFechaTipo[fecha]).forEach(sede => {
                Object.keys(resumenPorFechaTipo[fecha][sede]).forEach(tipo => {
                    resumenArray.push({
                        Fecha: fecha,
                        Sede: sede,
                        'Tipo de Menú': tipo,
                        Cantidad: resumenPorFechaTipo[fecha][sede][tipo]
                    });
                });
            });
        });

        const wsResumen = XLSX.utils.json_to_sheet(resumenArray);
        XLSX.utils.book_append_sheet(wb, wsResumen, "Totales por Día");

        const today = new Date().toISOString().split('T')[0];
        const sedeNombre = sedeFilter === 'todas' ? 'todas' : sedes.find(s => String(s.id) === String(sedeFilter))?.nombre || 'sede';
        XLSX.writeFile(wb, `pedidos_${sedeNombre}_${today}.xlsx`);
    };

    // Función para generar código ZPL directo para Zebra ZD421 (descarga .zpl)
    const generateZPL = (pedidosAImprimir = pedidosVisibles) => {
        if (!pedidosAImprimir || pedidosAImprimir.length === 0) {
            alert('No hay pedidos para generar ZPL.');
            return;
        }

        let zplCode = '';

        pedidosAImprimir.forEach(pedido => {
            zplCode += generateZPLFromConfig(pedido, etiquetaConfig);
        });

        // Crear archivo ZPL para descargar
        const blob = new Blob([zplCode], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `etiquetas_zebra_${new Date().toISOString().split('T')[0]}.zpl`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        alert(`✅ Archivo ZPL descargado para Zebra ZD421!

📋 Pasos para imprimir:
1. Conecta la Zebra ZD421 por USB o red
2. Envía el archivo .zpl a la impresora usando:
   - Zebra Setup Utilities (Windows)
   - Comando: copy archivo.zpl \\\\computadora\\zebra
   - O arrastra el archivo a la cola de impresión
3. Las etiquetas se imprimirán automáticamente

🔧 Si no funciona:
- Verifica que la Zebra esté configurada como impresora predeterminada
- Instala los drivers oficiales de Zebra
- Configura el tamaño: 4" x 2" (100mm x 50mm)`);
    };

    // Imprimir directamente en impresora Zebra USB usando QZ Tray
    const sendToQZ = async (pedidosAImprimir = pedidosVisibles) => {
        console.log('🖨️ sendToQZ ejecutándose...', pedidosAImprimir?.length, 'pedidos');

        if (!pedidosAImprimir || pedidosAImprimir.length === 0) {
            alert('No hay pedidos para imprimir.');
            return;
        }

        // Construir ZPL usando configuración personalizada
        let zplCode = '';

        pedidosAImprimir.forEach(pedido => {
            zplCode += generateZPLFromConfig(pedido, etiquetaConfig);
        });

        console.log('📝 ZPL generado:', zplCode.substring(0, 100) + '...');
        console.log('🔍 window.qz disponible:', !!window.qz);

        if (!window.qz) {
            alert('⚠️ QZ Tray no está instalado en este equipo.\n\n' +
                '📥 Para imprimir directamente desde el navegador:\n' +
                '1. Descargá QZ Tray desde: https://qz.io/download/\n' +
                '2. Instalalo y ejecutalo\n' +
                '3. Volvé a hacer clic en "Imprimir"\n\n' +
                '💾 Por ahora se descargará el archivo .zpl que podés enviar manualmente a la impresora.');
            // fallback: descargar
            const blob = new Blob([zplCode], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `etiquetas_zebra_${new Date().toISOString().split('T')[0]}.zpl`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            return;
        }

        try {
            // Intentar conectar a QZ Tray
            if (!window.qz.websocket.isActive()) {
                await window.qz.websocket.connect();
            }

            // Buscar impresoras ZPL disponibles (Zebra, Honeywell, etc)
            const printers = await window.qz.printers.find();
            console.log('Impresoras detectadas:', printers);

            // Obtener impresora guardada en localStorage
            const savedPrinter = localStorage.getItem('selectedLabelPrinter');
            let selectedPrinter = null;

            // Si hay impresora guardada y está disponible, usarla
            if (savedPrinter && printers.includes(savedPrinter)) {
                selectedPrinter = savedPrinter;
                console.log('Usando impresora guardada:', selectedPrinter);
            }

            // Si no hay impresora guardada o no está disponible, mostrar selector
            if (!selectedPrinter && printers.length > 0) {
                const printerList = printers.map((p, i) => `${i + 1}. ${p}`).join('\n');
                const selection = prompt(
                    `🖨️ Seleccioná tu impresora de etiquetas:\n\n` +
                    `${printerList}\n\n` +
                    `Ingresá el número (se guardará tu preferencia):\n` +
                    `O dejá vacío para descargar el .zpl`
                );

                if (selection && !isNaN(selection)) {
                    const index = parseInt(selection) - 1;
                    if (index >= 0 && index < printers.length) {
                        selectedPrinter = printers[index];
                        // Guardar preferencia
                        localStorage.setItem('selectedLabelPrinter', selectedPrinter);
                        console.log('Impresora guardada:', selectedPrinter);
                    }
                }

                if (!selectedPrinter) {
                    // Usuario canceló o no seleccionó válido → descargar
                    const blob = new Blob([zplCode], { type: 'text/plain' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `etiquetas_zebra_${new Date().toISOString().split('T')[0]}.zpl`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                    return;
                }
            }

            if (!selectedPrinter) {
                throw new Error('No se detectaron impresoras. Verificá que la impresora esté conectada y encendida.');
            }

            // Configurar e imprimir
            const config = window.qz.configs.create(selectedPrinter);
            const data = [{ type: 'raw', format: 'plain', data: zplCode }];
            await window.qz.print(config, data);

            console.log(`✅ ${pedidosAImprimir.length} etiqueta(s) enviadas a ${selectedPrinter}`);

            // Desconectar (opcional, puede quedar conectado)
            // await window.qz.websocket.disconnect();
        } catch (err) {
            console.error('Error imprimiendo con QZ Tray:', err);
            alert(`❌ Error al imprimir:\n${err.message || err}\n\n` +
                `🔧 Verificá que:\n` +
                `- QZ Tray esté ejecutándose\n` +
                `- La impresora esté conectada por USB o red\n` +
                `- Los drivers de la impresora estén instalados\n\n` +
                `💾 Se descargará el archivo .zpl como alternativa.`);

            // Fallback en caso de error: descargar
            const blob = new Blob([zplCode], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `etiquetas_zebra_${new Date().toISOString().split('T')[0]}.zpl`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        }
    };

    // Función para generar PDF descargable
    const generateHTMLPreview = (pedidosAImprimir = pedidosVisibles) => {
        if (!pedidosAImprimir || pedidosAImprimir.length === 0) {
            alert('No hay pedidos para generar PDF.');
            return;
        }

        const TAMANOS_ETIQUETA = {
            '100x50': { widthMm: 100, heightMm: 50, widthIn: 4, heightIn: 2, labelClassW: '3.8in', labelClassH: '1.8in' },
            '63x31': { widthMm: 63, heightMm: 31, widthIn: 2.48, heightIn: 1.22, labelClassW: '2.3in', labelClassH: '1.05in' },
        };
        const tamano = TAMANOS_ETIQUETA[etiquetaConfig?.tamanoEtiqueta] || TAMANOS_ETIQUETA['100x50'];

        const fechaVencimiento = new Date();
        fechaVencimiento.setDate(fechaVencimiento.getDate() + 7);
        const fechaVencimientoStr = fechaVencimiento.toLocaleDateString('es-ES');

        // HTML optimizado para descargar e imprimir
        const htmlContent = `<!DOCTYPE html>
<html>
<head>
    <title>Etiquetas Def-Software - ZD421</title>
    <style>
        @page { 
            size: ${tamano.widthIn}in ${tamano.heightIn}in; 
            margin: 0.05in; 
        }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
            font-family: Arial, sans-serif; 
            font-size: 10pt; 
            line-height: 1.2; 
        }
        .label {
            width: ${tamano.labelClassW};
            height: ${tamano.labelClassH};
            border: 2px solid #000;
            padding: 0.05in;
            display: flex;
            justify-content: space-between;
            page-break-after: always;
            box-sizing: border-box;
            margin-bottom: 0.05in;
        }
        .label:last-child { page-break-after: avoid; }
        .label-left { 
            flex: 1; 
            padding-right: 0.1in; 
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        }
        .label-right { 
            width: 1.2in; 
            display: flex;
            flex-direction: column;
            justify-content: center;
            text-align: center;
            border-left: 2px solid #000;
            padding: 0.05in;
        }
        .label-header { 
            font-weight: bold; 
            font-size: 12pt; 
            margin-bottom: 0.05in;
        }
        .user-name { 
            font-weight: bold; 
            font-size: 11pt; 
            margin-bottom: 0.03in;
        }
        .user-details { 
            font-size: 8pt; 
            margin-bottom: 0.02in;
        }
        .plato-info { 
            font-weight: bold; 
            font-size: 10pt; 
            margin-top: auto;
            border-top: 1px solid #000;
            padding-top: 0.03in;
        }
        .vence-label { 
            font-size: 8pt; 
            font-weight: bold;
            margin-bottom: 0.03in;
        }
        .fecha-valor { 
            font-size: 10pt; 
            font-weight: bold;
        }
    </style>
</head>
<body>
    ${pedidosAImprimir.map(pedido => `
                    <div class="label">
                        <div class="label-left">
                            <div>
                                <div class="label-header">Def-Software</div>
                                <div class="user-name">${pedido.nombreuser} ${pedido.apellido}</div>
                    <div class="user-details">${getSectorEtiqueta(pedido)}</div>
                    <div class="user-details">${getUbicacionEtiqueta(pedido)}</div>
                            </div>
                            <div class="plato-info">${pedido.nombre}</div>
            </div>
            <div class="label-right">
                <div class="vence-label">VENCE:</div>
                <div class="fecha-valor">${fechaVencimientoStr}</div>
            </div>
        </div>`).join('')}
</body>
</html>`;

        // Crear y descargar archivo HTML
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `etiquetas_${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        alert(`📄 Archivo HTML descargado!

📋 Para imprimir:
1. Abre el archivo HTML descargado
2. Presiona Ctrl+P (Imprimir)
3. Selecciona tu Zebra ZD421
4. Configura: Tamaño personalizado 4" x 2"
5. Sin márgenes o márgenes mínimos`);

    };

    // Función para generar PDF descargable con jsPDF usando configuración personalizada
    const generatePDF = (pedidosAImprimir = pedidosVisibles, autoPrint = false) => {
        if (!pedidosAImprimir || pedidosAImprimir.length === 0) {
            alert('No hay pedidos para generar PDF.');
            return;
        }

        const TAMANOS_ETIQUETA = {
            '100x50': { width: 100, height: 50 },
            '63x31': { width: 63, height: 31 },
        };
        const tamano = TAMANOS_ETIQUETA[etiquetaConfig?.tamanoEtiqueta] || TAMANOS_ETIQUETA['100x50'];

        // Crear PDF en formato de etiqueta configurado
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: [tamano.width, tamano.height]
        });

        pedidosAImprimir.forEach((pedido, index) => {
            if (index > 0) {
                doc.addPage([tamano.width, tamano.height], 'landscape');
            }

            const [anio, mes, dia] = String(pedido.fecha || '').split('-').map(Number);
            const fechaElaboracion = new Date(anio, mes - 1, dia);
            const fechaVencimiento = new Date(anio, mes - 1, dia + 1);
            const sectorEtiqueta = getSectorEtiqueta(pedido);
            const ubicacionEtiqueta = getUbicacionEtiqueta(pedido);
            const direccionEtiqueta = getDireccionEtiqueta(pedido);

            if (etiquetaConfig && etiquetaConfig.campos) {
                const camposConfigurados = asegurarCamposFecha(etiquetaConfig.campos);
                // Usar configuración personalizada
                camposConfigurados.filter(c => c.visible).forEach(campo => {
                    const x = campo.x;
                    const y = campo.y;
                    const fontSize = campo.fontSize || 12;

                    // Generar contenido según el tipo de campo
                    if (campo.id === 'separador') {
                        // Línea horizontal
                        doc.setLineWidth(0.3);
                        doc.line(x, y, x + 80, y);
                        return;
                    }

                    if (campo.id === 'qrCode' || campo.id === 'barcode') {
                        // QR y códigos de barras no se soportan fácilmente en jsPDF sin libs adicionales
                        // Podrías agregar una nota o dejarlo vacío
                        return;
                    }

                    // Caso general: texto
                    let texto = '';
                    switch (campo.id) {
                        case 'header':
                            texto = 'Def-Software';
                            doc.setFont('helvetica', 'bold');
                            break;
                        case 'nombre':
                            texto = `${campo.prefijo || ''}${pedido.nombreuser || ''}`;
                            doc.setFont('helvetica', 'normal');
                            break;
                        case 'apellido':
                            texto = `${campo.prefijo || ''}${pedido.apellido || ''}`;
                            doc.setFont('helvetica', 'normal');
                            break;
                        case 'nombreCompleto':
                            texto = `${campo.prefijo || ''}${pedido.nombreuser || ''} ${pedido.apellido || ''}`;
                            doc.setFont('helvetica', 'bold');
                            break;
                        case 'plato':

                            texto = `${campo.prefijo || ''}${pedido.nombre || ''}`;
                            doc.setFont('helvetica', 'bold');
                            break;
                        case 'tipoPlato':
                            texto = `${campo.prefijo || ''}${pedido.tipo || ''}`;
                            doc.setFont('helvetica', 'normal');
                            break;
                        case 'sector':
                            texto = `${campo.prefijo || ''}${sectorEtiqueta}`;
                            doc.setFont('helvetica', 'normal');
                            break;
                        case 'ubicacion':
                            texto = `${campo.prefijo || ''}${ubicacionEtiqueta}`;
                            doc.setFont('helvetica', 'normal');
                            break;
                        case 'sede':
                            texto = `${campo.prefijo || ''}${pedido.sede_nombre || ''}`;
                            doc.setFont('helvetica', 'normal');
                            break;
                        case 'sede_direccion':
                            texto = `${campo.prefijo || ''}${direccionEtiqueta}`;
                            doc.setFont('helvetica', 'normal');
                            break;
                        case 'fechaElaboracion':
                            texto = `${campo.prefijo || 'Elaborado: '}${fechaElaboracion.toLocaleDateString('es-AR')}`;
                            doc.setFont('helvetica', 'bold');
                            break;
                        case 'fechaVencimiento':
                            texto = `${campo.prefijo || 'Consumir hasta: '}${fechaVencimiento.toLocaleDateString('es-AR')}`;
                            doc.setFont('helvetica', 'bold');
                            break;
                        default:
                            texto = campo.label || '';
                            doc.setFont('helvetica', 'normal');
                    }

                    doc.setFontSize(fontSize);
                    // Ajuste de Y: jsPDF usa la línea de base (baseline).
                    // Para que 'y' sea el tope del texto, sumamos el tamaño de la fuente convertido a mm
                    const yAdjusted = y + (fontSize * 0.3527); // 1pt = 0.3527mm
                    doc.text(texto, x, yAdjusted);
                });
            } else {
                // Configuración predeterminada
                const fechaElaboracionStr = fechaElaboracion.toLocaleDateString('es-AR');
                const fechaVencimientoStr = fechaVencimiento.toLocaleDateString('es-AR');

                // Borde exterior
                doc.setLineWidth(0.5);
                doc.rect(2, 2, 96, 46);

                // Línea divisoria vertical
                doc.line(70, 2, 70, 48);

                // Sección izquierda
                doc.setFontSize(14);
                doc.setFont('helvetica', 'bold');
                doc.text('Def-Software', 5, 8);

                doc.setFontSize(12);
                const nombreUsr = (pedido.nombreuser || '').trim();
                const apellidoUsr = (pedido.apellido || '').trim();
                const fullUserName = `${nombreUsr} ${apellidoUsr}`.trim() || 'S/N';
                doc.text(fullUserName, 5, 15);

                doc.setFontSize(9);
                doc.setFont('helvetica', 'normal');
                const sectorLabel = sectorEtiqueta || 'S/S';
                const ubicacionLabel = ubicacionEtiqueta || 'S/U';
                doc.text(sectorLabel, 5, 20);
                doc.text(ubicacionLabel, 5, 24);

                // Línea separadora
                doc.line(5, 35, 68, 35);

                // Nombre del plato
                doc.setFontSize(11);
                doc.setFont('helvetica', 'bold');
                const platoNombre = String(pedido.nombre || 'Menú');
                const nombrePlatoFmt = platoNombre.length > 25
                    ? platoNombre.substring(0, 25) + '...'
                    : platoNombre;
                doc.text(nombrePlatoFmt, 5, 40);

                // Sección derecha (fecha de vencimiento)
                doc.setFontSize(8);
                doc.setFont('helvetica', 'bold');
                doc.text('Elaborado:', 75, 16, { align: 'center' });
                doc.setFontSize(10);
                doc.text(fechaElaboracionStr, 75, 22, { align: 'center' });
                doc.setFontSize(8);
                doc.text('Consumir hasta:', 75, 31, { align: 'center' });
                doc.setFontSize(10);
                doc.text(fechaVencimientoStr, 75, 37, { align: 'center' });
            }
        });

        if (autoPrint) {
            // Abrir el PDF en una ventana nueva y disparar el diálogo de impresión
            const pdfBlob = doc.output('blob');
            const pdfUrl = URL.createObjectURL(pdfBlob);
            const printWindow = window.open(pdfUrl, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                    setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
                };
            } else {
                // Si el navegador bloqueó el popup, descargar el PDF directamente
                const a = document.createElement('a');
                a.href = pdfUrl;
                a.download = `etiquetas_zebra_${new Date().toISOString().split('T')[0]}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(pdfUrl), 5000);
            }

        } else {
            // Descargar PDF
            doc.save(`etiquetas_zebra_${new Date().toISOString().split('T')[0]}.pdf`);

            alert(`✅ PDF generado exitosamente con tu plantilla personalizada!

📋 Para imprimir en Zebra ZD421:
1. Abrí el PDF descargado
2. Presioná Ctrl+P (Imprimir)
3. Seleccioná tu impresora Zebra ZD421
4. Configurá el tamaño: 100mm x 50mm (4" x 2")
5. Orientación: Horizontal
6. Márgenes: Ninguno o mínimos

💡 También podés usar cualquier impresora común.`);
        }
    };
    const handleSelectPedido = (pedidoId) => {
        setSelectedPedidos(prev => {
            if (prev.includes(pedidoId)) {
                return prev.filter(id => id !== pedidoId);
            } else {
                return [...prev, pedidoId];
            }
        });
    };

    const handleSelectAll = () => {
        const pedidosVisiblesIds = pedidosVisibles.map((pedido) => pedido.id_pedido);
        const estanTodosSeleccionados = pedidosVisiblesIds.length > 0 && pedidosVisiblesIds.every((id) => selectedPedidos.includes(id));

        if (estanTodosSeleccionados) {
            setSelectedPedidos((prev) => prev.filter((id) => !pedidosVisiblesIds.includes(id)));
        } else {
            setSelectedPedidos((prev) => Array.from(new Set([...prev, ...pedidosVisiblesIds])));
        }
    };

    const generateSelectedZPL = () => {
        const pedidosSeleccionados = pedidosVisibles.filter(p => selectedPedidos.includes(p.id_pedido));
        if (pedidosSeleccionados.length === 0) {
            alert('Por favor selecciona al menos un pedido');
            return;
        }
        generateZPL(pedidosSeleccionados);
    };

    const generateSelectedPDF = (autoPrint = false) => {
        const pedidosSeleccionados = pedidosVisibles.filter(p => selectedPedidos.includes(p.id_pedido));
        if (pedidosSeleccionados.length === 0) {
            alert('Por favor selecciona al menos un pedido');
            return;
        }
        generatePDF(pedidosSeleccionados, autoPrint);
    };

    const generateSelectedHTMLPreview = () => {
        const pedidosSeleccionados = pedidosVisibles.filter(p => selectedPedidos.includes(p.id_pedido));
        if (pedidosSeleccionados.length === 0) {
            alert('Por favor selecciona al menos un pedido');
            return;
        }
        generateHTMLPreview(pedidosSeleccionados);
    };

    return (
        <div className={`min-h-screen p-3 lg:p-4 font-sans transition-colors duration-300 flex flex-col ${isDarkMode ? "bg-slate-950 text-white" : "bg-slate-50 text-slate-800"}`}>

            {/* HEADER PREMIUM */}
            <div className={`sticky top-0 z-20 ${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"} rounded-2xl shadow-sm p-5 mb-4 flex flex-col xl:flex-row items-center justify-between border ring-1 ring-black/5 gap-6`}>
                <div className="flex items-center gap-4">
                    <div className="bg-gradient-to-br from-fuchsia-500 to-fuchsia-700 p-3 rounded-xl shadow-lg shadow-fuchsia-900/20">
                        <Ticket className="text-white w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-xl font-black tracking-tight leading-none mb-1">Listado de Pedidos</h1>
                        <p className={`text-[11px] ${isDarkMode ? "text-slate-400" : "text-slate-500"} font-black uppercase tracking-wider`}>Gestión de etiquetas y logística de viandas</p>
                    </div>
                </div>

                {/* ACTION BAR */}
                <div className="flex flex-wrap items-center justify-center lg:justify-end gap-2 xl:gap-3 w-full xl:w-auto">
                    {/* Configurador */}
                    <button
                        onClick={() => setShowConfigurador(true)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${isDarkMode ? "bg-slate-800 text-slate-300 hover:bg-slate-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                    >
                        <Settings2 className="w-3.5 h-3.5" /> Configurar Etiquetas
                    </button>

                    <div className="h-6 w-px bg-slate-700/30 hidden md:block mx-1"></div>

                    {/* Zebra / ZPL */}
                    <div className="flex gap-1.5">
                        <button
                            onClick={() => generateZPL(pedidosVisibles)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-950/20 flex items-center gap-2 transition-all disabled:opacity-50"
                            disabled={pedidosVisibles.length === 0}
                        >
                            <Printer className="w-3.5 h-3.5" /> Imprimir ZPL
                        </button>

                        <button
                            onClick={() => generateSelectedZPL()}
                            className={`px-4 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${selectedPedidos.length > 0
                                ? "bg-indigo-500/10 text-indigo-400 border border-indigo-500/50 hover:bg-indigo-500/20"
                                : "bg-slate-800/10 text-slate-600 border border-slate-800/20 cursor-not-allowed"
                                }`}
                            disabled={selectedPedidos.length === 0}
                        >
                            ({selectedPedidos.length}) Selecc.
                        </button>
                    </div>

                    {/* PDF */}
                    <div className="flex gap-1.5">
                        <button
                            onClick={() => generatePDF(pedidosVisibles, true)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-emerald-950/20 flex items-center gap-2 transition-all disabled:opacity-50"
                            disabled={pedidosVisibles.length === 0}
                        >
                            <FileText className="w-3.5 h-3.5" /> Imprimir PDF
                        </button>
                        <button
                            onClick={() => generateSelectedPDF(true)}
                            className={`px-4 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${selectedPedidos.length > 0
                                ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/50 hover:bg-emerald-500/20"
                                : "bg-slate-800/10 text-slate-600 border border-slate-800/20 cursor-not-allowed"
                                }`}
                            disabled={selectedPedidos.length === 0}
                        >
                            ({selectedPedidos.length}) Selecc.
                        </button>

                    </div>

                    {/* EXCEL */}
                    <button
                        onClick={() => exportToExcel()}
                        className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-orange-950/20 flex items-center gap-2 transition-all"
                    >
                        <Download className="w-3.5 h-3.5" /> Excel
                    </button>
                </div>
            </div>

            {/* FILTROS */}
            <div className={`sticky top-[88px] z-10 ${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"} rounded-2xl shadow-sm p-4 mb-4 border flex flex-wrap items-center gap-4`}>
                <Building2 className={`w-5 h-5 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`} />
                <label className="font-black text-[10px] uppercase tracking-wider">Filtrar por Sede:</label>
                <select
                    value={sedeFilter}
                    onChange={(e) => setSedeFilter(e.target.value)}
                    className={`px-4 py-2 rounded-xl font-medium text-sm ${isDarkMode ? "bg-slate-800 text-white border-slate-700" : "bg-slate-50 text-slate-900 border-slate-200"} border focus:ring-2 focus:ring-fuchsia-500 outline-none`}
                >
                    <option value="todas">Todas las Sedes</option>
                    {(sedes || []).map(sede => (
                        <option key={sede.id} value={sede.id}>{sede.nombre}</option>
                    ))}
                </select>
                <label className="font-black text-[10px] uppercase tracking-wider">Tipo:</label>
                <select
                    value={tipoFilter}
                    onChange={(e) => setTipoFilter(e.target.value)}
                    className={`px-4 py-2 rounded-xl font-medium text-sm ${isDarkMode ? "bg-slate-800 text-white border-slate-700" : "bg-slate-50 text-slate-900 border-slate-200"} border focus:ring-2 focus:ring-fuchsia-500 outline-none`}
                >
                    <option value="todos">Todos los Tipos</option>
                    {tiposDisponibles.map((tipo) => (
                        <option key={tipo} value={tipo}>{tipo}</option>
                    ))}
                </select>
                <label className="font-black text-[10px] uppercase tracking-wider">Sector:</label>
                <select
                    value={sectorFilter}
                    onChange={(e) => setSectorFilter(e.target.value)}
                    className={`px-4 py-2 rounded-xl font-medium text-sm ${isDarkMode ? "bg-slate-800 text-white border-slate-700" : "bg-slate-50 text-slate-900 border-slate-200"} border focus:ring-2 focus:ring-fuchsia-500 outline-none`}
                >
                    <option value="todos">Todos los Sectores</option>
                    {sectoresDisponibles.map((sector) => (
                        <option key={sector} value={sector}>{sector}</option>
                    ))}
                </select>
                <div className="flex items-center gap-2">
                    <label className="font-black text-[10px] uppercase tracking-wider">Desde:</label>
                    <input
                        type="date"
                        value={fechaDesde}
                        onChange={(e) => setFechaDesde(e.target.value)}
                        className={`px-3 py-2 rounded-xl font-medium text-sm ${isDarkMode ? "bg-slate-800 text-white border-slate-700" : "bg-slate-50 text-slate-900 border-slate-200"} border focus:ring-2 focus:ring-fuchsia-500 outline-none`}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <label className="font-black text-[10px] uppercase tracking-wider">Hasta:</label>
                    <input
                        type="date"
                        value={fechaHasta}
                        onChange={(e) => setFechaHasta(e.target.value)}
                        className={`px-3 py-2 rounded-xl font-medium text-sm ${isDarkMode ? "bg-slate-800 text-white border-slate-700" : "bg-slate-50 text-slate-900 border-slate-200"} border focus:ring-2 focus:ring-fuchsia-500 outline-none`}
                    />
                </div>
                <span className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                    {pedidosVisibles.length} pedidos en el rango
                </span>
                {(fechaDesde || fechaHasta) && (
                    <span className={`text-xs font-semibold ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                        {`${fechaDesde || 'inicio'} al ${fechaHasta || 'fin'}`}
                    </span>
                )}
            </div>

            {/* TABLE AREA */}
            <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"} rounded-3xl shadow-sm border overflow-hidden flex-1`}>
                <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-220px)]">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 z-10">
                            <tr className={`${isDarkMode ? "bg-black/20 text-slate-500 border-slate-800" : "bg-slate-50 text-slate-400 border-slate-100"} border-b text-[10px] font-black uppercase tracking-[0.15em]`}>
                                <th className="px-6 py-4 w-10">
                                    <input
                                        type="checkbox"
                                        checked={pedidosVisibles.length > 0 && pedidosVisibles.every((pedido) => selectedPedidos.includes(pedido.id_pedido))}
                                        onChange={handleSelectAll}
                                        className="rounded-md accent-fuchsia-500 w-4 h-4 cursor-pointer"
                                    />
                                </th>
                                <th className="px-6 py-4">Menú Seleccionado</th>
                                <th className="px-6 py-4">Fecha</th>
                                <th className="px-6 py-4">Usuario</th>
                                <th className="px-6 py-4">Ubicación / Sector</th>
                                <th className="px-6 py-4">Observación</th>
                                <th className="px-6 py-4 text-center">Etiquetas</th>
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${isDarkMode ? "divide-slate-800" : "divide-slate-100"}`}>
                            {pedidosVisibles.map((order) => (
                                <tr key={order.id_pedido} className={`group transition-all hover:bg-fuchsia-500/[0.02] ${selectedPedidos.includes(order.id_pedido) ? "bg-fuchsia-500/[0.05]" : ""}`}>
                                    <td className="px-6 py-4">
                                        <input
                                            type="checkbox"
                                            checked={selectedPedidos.includes(order.id_pedido)}
                                            onChange={() => handleSelectPedido(order.id_pedido)}
                                            className="rounded-md accent-fuchsia-500 w-4 h-4 cursor-pointer"
                                        />
                                    </td>
                                    <td className="px-6 py-4">
                                        <div>
                                            <p className="text-sm font-black tracking-tight" style={{ color: isDarkMode ? '#e879f9' : '#701a75' }}>{order.nombre}</p>
                                            <p className={`text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? "text-slate-400" : "text-slate-400"}`}>{order.tipo}</p>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500">
                                            <Calendar className="w-3.5 h-3.5 text-fuchsia-500" />
                                            {order.fecha}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2 text-[11px] font-black tracking-tight text-slate-300">
                                            <div className="w-7 h-7 rounded-full bg-slate-800 flex items-center justify-center text-[9px] font-black border border-slate-700">
                                                {order.nombreuser?.charAt(0)}{order.apellido?.charAt(0)}
                                            </div>
                                            <div>
                                                <p className={isDarkMode ? "text-slate-200" : "text-slate-800"}>{order.nombreuser} {order.apellido}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500">
                                                <MapPin className="w-3 h-3 text-emerald-500" />
                                                {getDireccionEtiqueta(order)}
                                            </div>
                                            <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500">
                                                <Building2 className="w-3 h-3 text-orange-400" />
                                                {order.sector}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <p className={`text-[11px] italic font-medium max-w-[200px] truncate ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                                            {order.observacion || 'Sin observaciones'}
                                        </p>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center justify-center gap-2">
                                            <Tooltip title="Imprimir ZPL">
                                                <button
                                                    onClick={() => generateZPL([order])}
                                                    className={`p-2 rounded-xl border transition-all ${isDarkMode ? "bg-slate-800 border-slate-700 text-slate-400 hover:text-indigo-400" : "bg-white border-slate-200"
                                                        }`}
                                                >
                                                    <Printer className="w-4 h-4" />
                                                </button>
                                            </Tooltip>
                                            <Tooltip title="Imprimir PDF">
                                                <button
                                                    onClick={() => generatePDF([order], true)}
                                                    className={`p-2 rounded-xl border transition-all ${isDarkMode ? "bg-slate-800 border-slate-700 text-slate-400 hover:text-emerald-400" : "bg-white border-slate-200"
                                                        }`}
                                                >
                                                    <FileText className="w-4 h-4" />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {pedidosVisibles.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-20 grayscale opacity-40">
                            <Ticket className="w-12 h-12 mb-3 text-slate-400" />
                            <p className="text-xs font-black uppercase tracking-widest text-slate-400">No hay pedidos para la semana próxima en esta sede</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal configurador de etiquetas */}
            <ConfiguradorEtiquetas
                isOpen={showConfigurador}
                onClose={() => setShowConfigurador(false)}
                onSaveConfig={(config) => {
                    console.log('📥 Config guardada desde modal:', config);
                    setEtiquetaConfig(config);
                    setTimeout(() => {
                        setShowConfigurador(false);
                    }, 100);
                }}
                pedidosReales={pedidos}
            />
        </div>
    );
}

export default AbmPedidos;
